import { useReportsRequestRRA } from '@/api/WhisApi/generated';
import { showToast } from '@/components/Toast/toast.service';
import { useAppTranslation } from '@/hooks/useAppTranslation';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'expo-router';
import { useCallback } from 'react';

interface UseRequestRraOptions {
  reportId: string;
  isBoardRoute: boolean;
}

interface UseRequestRraResult {
  isRequestingRra: boolean;
  requestRra: () => void;
}

export const useRequestRra = ({
  reportId,
  isBoardRoute,
}: UseRequestRraOptions): UseRequestRraResult => {
  const { t } = useAppTranslation();
  const queryClient = useQueryClient();
  const router = useRouter();
  const { mutate: requestRraMutation, isPending: isRequestingRra } =
    useReportsRequestRRA();

  const requestRra = useCallback((): void => {
    requestRraMutation(
      { reportId },
      {
        onSuccess: (updatedReport) => {
          queryClient.invalidateQueries({
            queryKey: ['api', 'reports'],
          });
          router.replace({
            pathname: isBoardRoute
              ? '/(tabs)/board/reports/[reportId]/edit/request-rra/success'
              : '/(tabs)/reports/[reportId]/edit/request-rra/success',
            params: { reportId: updatedReport.id },
          });
        },
        onError: (error) => {
          console.error('RRA request failed', error);
          showToast({
            type: 'error',
            title: t('reportDetail.toasts.requestRraError'),
          });
        },
      },
    );
  }, [isBoardRoute, queryClient, reportId, requestRraMutation, router, t]);

  return { isRequestingRra, requestRra };
};
